import { useEffect, useState } from 'react';
import type { z } from 'zod';
import { ThemePref } from '@timato/shared';

export type ThemePrefValue = z.infer<typeof ThemePref>;
const KEY = 'timato.theme';

export function readThemePref(): ThemePrefValue {
  try { const v = localStorage.getItem(KEY); return ThemePref.safeParse(v).success ? (v as ThemePrefValue) : 'system'; }
  catch { return 'system'; }
}
export function resolveTheme(pref: ThemePrefValue): 'light' | 'dark' {
  if (pref !== 'system') return pref;
  return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
export function applyTheme(pref: ThemePrefValue) {
  document.documentElement.setAttribute('data-theme', resolveTheme(pref));
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', resolveTheme(pref) === 'dark' ? '#0E1013' : '#FAFAF8');
}
export function writeThemePref(pref: ThemePrefValue) {
  try { localStorage.setItem(KEY, pref); } catch { /* private mode: still apply for this session */ }
  applyTheme(pref);
}

/** Theme preference with live system tracking. The Dexie settings row mirrors this in Phase 1. */
export function useThemePref() {
  const [pref, setPref] = useState<ThemePrefValue>(() => readThemePref());
  useEffect(() => {
    applyTheme(pref);
    if (pref !== 'system') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);
  return [pref, (p: ThemePrefValue) => { writeThemePref(p); setPref(p); }] as const;
}
